import {RefetchQueriesInclude, useApolloClient} from '@apollo/client';
import {
	DefaultButton,
	Panel,
	PanelType,
	PrimaryButton,
	Separator,
	Toggle,
} from '@fluentui/react';
import {ConfirmDeleteDialog, DialogState, HideDialog} from 'components/Dialogs';
import {LoadWrapper} from 'components/LoadWrapper';
import {MarketNamingTemplateField} from 'components';
import {
	ControlledDropdown,
	ControlledTagPicker,
	ControlledTextField,
	ControlledToggle,
} from 'components/hookForms';
import {ControlledRichtextEditor} from 'components/hookForms/ControlledRichtextEditor';
import {useGetConvolutesQuery} from 'features/AdminSection/hooks/convolutes.generated';
import {useGetGdprRuleQuery} from 'features/AdminSection/hooks/gdprRules.generated';
import {
	LabelWithTooltip,
	ProviderThatEnablesGettingTooltipsFromContext,
} from 'features/localizedTooltips';
import {mapToRef} from 'helpers';
import {useFileUpload} from 'hooks';
import React from 'react';
import {useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {ConvoluteType, Regulation} from 'types';
import {RegulationsContextProps} from '../context';
import {
	GetRegulationFormDataQuery,
	GetRegulationQuery,
	useCreateRegulationMutation,
	useDeleteRegulationMutation,
	useGetRegulationFormDataQuery,
	useUpdateRegulationMutation,
} from '../hooks';
import {useUpdateRegulationAttachmentsMutation} from '../hooks/updateRegulationAttachments.generated';
import {RegulationsTooltipTranslationProvider} from './RegulationsTooltipTranslationProvider';
import {
	RegulationFormMode,
	SetRegulationFormMode,
} from './useRegulationFormMode';
import {useRegulationFormTranslation} from './useRegulationFormTranslation';
import {alphabeticalSort} from 'helpers/sortHelpers';
import {useCheckCanBeHiddenLazyQuery} from '../hooks/useCheckCanBeHidden.generated';
import {useUserContext} from 'authentication/UserContext';

export interface RegulationFormProps {
	dialogState: DialogState;
	hideDialog: HideDialog;
	setMode: SetRegulationFormMode;
	mode: RegulationFormMode;
	regulation?:
		| RegulationsContextProps['selectedRegulation']
		| GetRegulationQuery['regulation'];
	queriesToRefetchAfterSaveOrDelete: RefetchQueriesInclude;
}

export const RegulationForm = ({
	dialogState,
	hideDialog,
	setMode,
	mode,
	regulation,
	queriesToRefetchAfterSaveOrDelete,
}: RegulationFormProps) => {
	const {t} = useRegulationFormTranslation();
	const [createRegulation] = useCreateRegulationMutation();
	const [updateRegulation] = useUpdateRegulationMutation();
	const [deleteRegulation] = useDeleteRegulationMutation();
	const [checkCanBeHidden, {data: dataCanBeHidden}] =
		useCheckCanBeHiddenLazyQuery();
	const [updateRegulationAttachments] =
		useUpdateRegulationAttachmentsMutation();
	const [showAttachments, setShowAttachments] = React.useState(false);
	const {isAvk, isBav, isVko} = useUserContext();
	const isAvkOrBav = isAvk || isBav;
	const {data: convolutes} = useGetConvolutesQuery();

	const convolute = React.useMemo(
		() =>
			convolutes?.convolutes?.find(
				c => c.convoluteType === ConvoluteType.Regulation,
			),
		[convolutes],
	);

	const {data: gdprRule} = useGetGdprRuleQuery({
		variables: {
			id: convolute?.gdprRule?.id || '',
		},
	});

	const rule = React.useMemo(() => gdprRule?.gdprRule, [gdprRule]);

	const navigate = useNavigate();

	const apolloClient = useApolloClient();

	const handleDeleteClick = React.useCallback(() => {
		if (regulation?.id) {
			deleteRegulation({
				variables: {
					input: {
						id: regulation.id,
					},
				},
				refetchQueries: queriesToRefetchAfterSaveOrDelete,
			});
			navigate(0);
		}

		hideDialog();
	}, [regulation, hideDialog]);

	const {handleSubmit, control, reset, watch} = useForm<Regulation>({
		reValidateMode: 'onSubmit',
		mode: 'onBlur',
	});

	const selectedMarkets = watch('markets') ?? [];

	React.useEffect(() => {
		reset(regulation ?? {});
	}, [regulation]);

	React.useEffect(() => {
		if (mode !== RegulationFormMode.None && regulation) {
			checkCanBeHidden({
				variables: {regulationId: regulation.id!},
			});
		}
	}, [regulation, mode]);

	const {attachments, FileUploadComponent} = useFileUpload(
		(regulation ?? undefined) as any,
		{
			enableVkoOnlyOption: true,
		},
	);

	const onRegulationSaved = React.useCallback(
		async (regId?: string) => {
			if (regId && attachments) {
				await updateRegulationAttachments({
					variables: {
						input: {
							regulationId: regId,
							attachments: attachments?.map(f => ({
								attachmentId: f.file.name,
								file: f.file,
								categoryId: f.attachmentCategoryId,
								isVkoOnly: f.isVkoOnly,
							})),
						},
					},
				});
			}

			await apolloClient.refetchQueries({
				include: queriesToRefetchAfterSaveOrDelete,
			});
		},
		[attachments, apolloClient],
	);

	const closeAndResumePolling = React.useCallback(() => {
		setMode(RegulationFormMode.None);
	}, []);

	const handleSaveClick = React.useCallback(() => {
		handleSubmit(async input => {
			if (dataCanBeHidden === null) {
				return;
			}
			switch (mode) {
				case RegulationFormMode.Create:
					createRegulation({
						variables: {
							input: {
								regulationNumber: input.regulationNumber,
								name: input.name,
								mainKeywordRefs: mapToRef(input?.mainKeywords),
								marketRefs: mapToRef(input?.markets),
								standardPlusPcmsClusterRefs: mapToRef(
									input?.standardPlusPcmsClusters,
								),
								icsCodeRefs: mapToRef(input?.icsCodes),
								hidden: input.hidden,
								summary: input.summary,
								willBeAnonymized: rule?.anonymize || false,
								deletionPeriod: rule?.deletionPeriod || 0,
								isMachineRegulation: isAvkOrBav,
								source: input.source,
							},
						},
					}).then(res => {
						const id = res.data?.createRegulation.regulation.id;
						if (id) {
							onRegulationSaved(id);
							navigate(`/regulations/${id}`);
						}
					});
					break;
				case RegulationFormMode.Update:
					if (!regulation?.id) {
						break;
					}
					await updateRegulation({
						variables: {
							input: {
								regulationNumber: input.regulationNumber,
								name: input.name,
								id: regulation.id,
								mainKeywordRefs: mapToRef(input?.mainKeywords),
								marketRefs: mapToRef(input?.markets),
								standardPlusPcmsClusterRefs: mapToRef(
									input?.standardPlusPcmsClusters,
								),
								icsCodeRefs: mapToRef(input?.icsCodes),
								hidden: input.hidden,
								summary: input.summary,
								source: input.source,
							},
						},
					}).then(async res => {
						const id = res.data?.updateRegulation.regulation.id;
						if (id) {
							await onRegulationSaved(id);
						}
					});
					navigate(0);
					break;
				default:
					break;
			}
			closeAndResumePolling();
		})();
	}, [mode, attachments, navigate, rule]);

	const {data, loading} = useGetRegulationFormDataQuery();
	const {
		marketData,
		mainKeywordData,
		standardPlusPcmsClusterData,
		icsCodeData,
	} = React.useMemo(() => mapFormEdgeNodes(data), [data]);

	const RegulationDetails =
		mode === RegulationFormMode.Update
			? t('EditRegulation')
			: t('EditEntity', {selectedRegulationName: regulation?.name});

	const onRenderFooterContent: any = () => (
		<form>
			<PrimaryButton styles={buttonStyles} onClick={handleSaveClick}>
				{mode === RegulationFormMode.Create
					? t('CreateButton')
					: t('UpdateButton')}
			</PrimaryButton>
			<DefaultButton onClick={closeAndResumePolling}>
				{t('CancelButton')}
			</DefaultButton>
		</form>
	);

	return (
		<>
			<Panel
				isLightDismiss
				isOpen={mode !== RegulationFormMode.None}
				onDismiss={closeAndResumePolling}
				closeButtonAriaLabel='Close'
				headerText={
					mode === RegulationFormMode.Create
						? t('CreateRegulationHeader')
						: `${RegulationDetails}`
				}
				type={PanelType.medium}
				onRenderFooterContent={onRenderFooterContent}
				isFooterAtBottom={true}
				styles={{main: {overflowY: 'hidden'}}}
			>
				<LoadWrapper loading={loading}>
					<RegulationsTooltipTranslationProvider>
						<ProviderThatEnablesGettingTooltipsFromContext>
							<ControlledTagPicker
								control={control}
								name={'markets'}
								label={t('MarketsTagPickerLabel')}
								selectableItems={marketData}
								getKey={item => item.id}
								getName={item => item.name}
								rules={{required: t('MarketFieldRequired')}}
							/>
							<MarketNamingTemplateField
								markets={selectedMarkets}
								namingHint={t('RegulationNumberNamingHint')}
								templatesToUse='regulationNamingTemplates'
								label={t('FreeText')}
							>
								{({validateFieldValue, isMarketSelected, placeholder}) => (
									<ControlledTextField
										required={true}
										label={t('RegulationNumberFieldLabel')}
										control={control}
										name={'regulationNumber'}
										rules={{
											required: t('RegulationNumberFieldRequired'),
											validate: validateFieldValue,
										}}
										disabled={!isMarketSelected}
										placeholder={placeholder}
									/>
								)}
							</MarketNamingTemplateField>
							<ControlledTextField
								required={true}
								label={t('NameFieldLabel')}
								control={control}
								name={'name'}
								rules={{required: t('NameFieldRequired')}}
							/>
							<ControlledRichtextEditor
								control={control}
								name={'summary'}
								label={t('SummaryFieldLabel')}
								rules={{required: t('SummaryFieldRequired')}}
							/>
							{/* <ControlledTagPicker
								control={control}
								name={'standardPlusPcmsClusters'}
								label={t('StandardPlusPcmsClustersTagPickerLabel')}
								selectableItems={standardPlusPcmsClusterData}
								getKey={item => item.id}
								getName={item => item.name}
								rules={{required: t('StandardPlusPcmsClusterFieldRequired')}}
							/> */}
							{/* <ControlledTagPicker
								control={control}
								name={'mainKeywords'}
								label={t('MainKeywordsTagPickerLabel')}
								selectableItems={mainKeywordData}
								getKey={item => item.id}
								getName={item => item.name}
								rules={{required: t('MainKeywordFieldRequired')}}
							/> */}
							<ControlledTextField
								control={control}
								name={'fake-field-1'}
								label={'Keyword / Related Legal Requirement'}
							/>
							{isAvkOrBav && (
								<>
									<ControlledTagPicker
										control={control}
										name='icsCodes'
										label={t('IcsCodesTagPickerLabel')}
										selectableItems={icsCodeData || []}
										getKey={item => item.id}
										getName={item => item.name}
										itemLimit={1}
									/>
									<ControlledTextField
										control={control}
										name='source'
										label={t('SourceFieldLabel')}
									/>
								</>
							)}
							<Separator />
							<ControlledToggle
								control={control}
								name={'hidden'}
								label={t('Hidden')}
								disabled={dataCanBeHidden?.canRegulationBeHidden === false}
							/>
							{dataCanBeHidden?.canRegulationBeHidden === false &&
								'Diese Vorschrift kann nicht versteckt werden. Es gibt mindestens eine Vorschriftenversion, die schon finalisiert wurde.'}
							<Separator />
							<LabelWithTooltip translationKey='attachments'>
								{t('AttachmentsFieldLabel')}
							</LabelWithTooltip>
							<Toggle onChange={() => setShowAttachments(!showAttachments)} />
							{showAttachments ? <FileUploadComponent /> : <div></div>}
							<Separator />
							<ControlledTextField
								control={control}
								name={'fake-field-2'}
								label={'E2e-Id'}
							/>
							<ControlledTextField
								control={control}
								name={'fake-field-3'}
								label={'E2e-Specific'}
							/>
							<ControlledTextField
								control={control}
								name={'fake-field-4'}
								label={'Policy Monitoring (to be imported)'}
							/>
							<ControlledTextField
								control={control}
								name={'fake-field-5'}
								label={'Responsibility - Working / Implementation Team'}
							/>
							<ControlledTextField
								control={control}
								name={'fake-field-6'}
								label={'Responsibility - Working / Implementation Team Members'}
							/>
							<ControlledDropdown
								control={control}
								name={'fake-field-7'}
								label={'Impact'}
								options={['1 Low', '4 High'].map(name => ({
									key: name,
									text: name,
								}))}
							/>
							<ControlledDropdown
								control={control}
								name={'fake-field-8'}
								label={'Implementation Complexity'}
								options={['1 Low', '4 High'].map(name => ({
									key: name,
									text: name,
								}))}
							/>
							<ControlledDropdown
								control={control}
								name={'fake-field-9'}
								label={'Evaluation Matrix'}
								options={['1', '2', '3', '4'].map(name => ({
									key: name,
									text: name,
								}))}
							/>
						</ProviderThatEnablesGettingTooltipsFromContext>
					</RegulationsTooltipTranslationProvider>
				</LoadWrapper>
			</Panel>
			<ConfirmDeleteDialog {...dialogState} onConfirm={handleDeleteClick} />
		</>
	);
};

const mapFormEdgeNodes = (data?: GetRegulationFormDataQuery) => {
	return {
		marketData: splitSort(data?.markets || []),
		mainKeywordData: alphabeticalSort(data?.activeKeywords || [], 'name'),
		standardPlusPcmsClusterData: data?.standardPlusPcmsClusters || [],
		vehicleCategorieData: data?.vehicleCategories || [],
		driveVariantData: data?.driveVariants || [],
		icsCodeData: data?.icsCodes || [],
	};
};

const splitSort = (arrObjects: any[]) => {
	const maxID = 100000;
	const objectsUnsorted = arrObjects ?? [];
	const objectsSorted = objectsUnsorted
		.slice()
		.sort((a, b) => (a.sortPriority ?? maxID) - (b.sortPriority ?? maxID));
	return objectsSorted;
};

const buttonStyles = {
	root: {
		marginRight: 8,
	},
};
